import axios from "axios";
import { API_BASE_PATHS, API_ROUTES, IAdServerBannerRequestDto } from "../libs";
import { buildRequestConfig } from "./buildRequestConfig";
import { IAdServerRollAdRequestDto, IAdServerUpdateCampaignRequestDto } from "../libs/models/ad-server";

class AdServerService {
    async getAllBanners() {
        try {
            const config = await buildRequestConfig();
            const response = await axios.get(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.AD_SERVER_API}/${API_ROUTES.STATIC_AD}`,
                { ...config }
            );

            return response.data.data;
        } catch (err: any) {
            console.log(
                "There is an error while fetching ad server banners",
                err
            );
            throw new Error(err);
        }
    }

    async createBanner(adServerBannerRequest: IAdServerBannerRequestDto) {
        try {
            const config = await buildRequestConfig();
            const response = await axios.post(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.AD_SERVER_API}/${API_ROUTES.STATIC_AD}`,
                adServerBannerRequest,
                { ...config }
            );

            return response.data;
        } catch (err: any) {
            console.log(
                "There is an error while creating a ad server banner",
                err
            );
            throw new Error(err);
        }
    }

    async deleteBanner(ad_id: string, s3_key: string) {
        try {
            const config = await buildRequestConfig();
            const response = await axios.delete(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.AD_SERVER_API}/${API_ROUTES.STATIC_AD}/${ad_id}/${s3_key}`,
                { ...config }
            );

            return response.data;
        } catch (err: any) {
            console.log(
                "There is an error while deleting a ad server banner",
                err
            );
            throw new Error(err);
        }
    }

    async updateBanner(adServerBannerRequest: IAdServerBannerRequestDto) {
        try {
            const config = await buildRequestConfig();
            const updateRequest = {
                adImageUrl: adServerBannerRequest.adImageUrl,
                adImageAlttext: adServerBannerRequest.adImageAlttext,
                adPosition: adServerBannerRequest.adPosition,
                adStatus: adServerBannerRequest.adStatus,
            };

            const response = await axios.put(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.AD_SERVER_API}/${API_ROUTES.STATIC_AD}/${adServerBannerRequest.adId}`,
                updateRequest,
                { ...config }
            );

            return response.data;
        } catch (err: any) {
            console.log(
                "There is an error while updating a ad server banner",
                err
            );
            throw new Error(err);
        }
    }

    async getAllCampaigns() {
        try {
            const config = await buildRequestConfig();
            const response = await axios.get(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.AD_SERVER_API}/${API_ROUTES.AD_CAMPAIGN}`,
                { ...config }
            );

            return response.data.data;
        } catch (err: any) {
            console.log(
                "There is an error while fetching ad server banners",
                err
            );
            throw new Error(err);
        }
    }

    async createCampaigns(adServerRollAdRequest: IAdServerRollAdRequestDto): Promise<any> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.post(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.AD_SERVER_API}/${API_ROUTES.AD_CAMPAIGN}`,
                adServerRollAdRequest,
                { ...config }
            );
            return response.data;
        } catch (err: any) {
            console.log("There is an error while creating an ad server campaign", err);
            throw new Error(err);
        }
    }
    async getCampaignById(campaignId: string): Promise<IAdServerUpdateCampaignRequestDto> {
        try {
            const config = await buildRequestConfig();
            const { data: campaignData } = await axios.get(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.AD_SERVER_API}/${API_ROUTES.AD_CAMPAIGN}/${campaignId}`,
                { ...config }
            );

            return campaignData.data;
        } catch (err: any) {
            console.error("Error fetching campaign:", err);
            const errorMessage =
                err.response?.data?.error?.message ??
                (typeof err.response?.data?.error === "string" ? err.response.data.error : "An unexpected error occurred.");

            throw new Error(errorMessage);
        }
    }

    async updateCampaigns(
        campaignId?: string,
        requestDto?: IAdServerUpdateCampaignRequestDto
    ): Promise<any> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.put(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.AD_SERVER_API}/${API_ROUTES.AD_CAMPAIGN}/${campaignId}`,
                { ...requestDto },
                { ...config }
            );
            return response.data;
        } catch (err: any) {
            console.log("There is an error while updating the campaign", err);
            throw new Error(err);
        }
    }

    async deleteCampaigns(campaignId: string) {
        try {
            const config = await buildRequestConfig();
            const response = await axios.delete(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.AD_SERVER_API}/${API_ROUTES.AD_CAMPAIGN}/${campaignId}`,
                { ...config }
            );

            return response.data;
        } catch (err: any) {
            console.error("Error deleting campaign:", err);
            throw new Error(err?.response?.data?.message || "Failed to delete campaign");
        }
    }

}

export const adServerService = new AdServerService();